import React, { useState } from 'react';
import { useClient } from '../context/ClientContext';
import { useUser } from '../context/UserContext';
import { useLeads } from '../hooks/useLeads';
import EmailOptionsModal from './EmailOptionsModal';
import ClientForm from './ClientForm';

// import ClientDetailModal from './ClientDetailModal'; // Removed - using SimpleLeadDetailModal for both
import SimpleLeadDetailModal from './SimpleLeadDetailModal';
// Client type agora vem do contexto
import { useToast } from '../hooks/use-toast';

import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Phone,
  Mail, 
  Users,
  UserPlus,
  TrendingUp,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  UserCheck,
  Eye,
  Archive
} from 'lucide-react';


const ClientList: React.FC = () => {
  const { toast } = useToast();
  const { getUserById } = useUser();
  const { 
    clients, 
    
    addClient, 
    updateClient, 
    deleteClient,
    getClientsByStatus
  } = useClient();
  
  const { leads: dbLeads, deleteLead: deleteLeadFromDB, loading: leadsLoading, convertLeadToClient: convertLeadDB, updateLeadStatus } = useLeads();
  
  const [activeTab, setActiveTab] = useState<'clients' | 'leads' | 'lost-leads'>('clients');
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState<any | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedLeadForEmail, setSelectedLeadForEmail] = useState<any>(null);
  // Removed separate client detail modal state - using lead modal for both
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [selectedLeadForDetail, setSelectedLeadForDetail] = useState<any>(null);


  // Usar leads do banco de dados (Supabase) em vez do contexto local
  const allLeads = dbLeads || [];
  
// Filtrar leads por status
const activeLeads = allLeads.filter(lead => 
  lead.status !== 'lost' && lead.status !== 'converted' && lead.status !== 'archived' && lead.status !== 'perdido'
);
const lostLeads = allLeads.filter(lead => 
  lead.status === 'archived' || lead.status === 'lost' || lead.status === 'perdido'
);
// Remover leads convertidos da aba clientes - eles devem estar como clients reais

  // Filtrar clientes
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (client.phone && client.phone.includes(searchTerm));
    
    const matchesStatus = !filterStatus || client.status === filterStatus;
    const matchesSource = !filterSource || client.source === filterSource;
    
    return matchesSearch && matchesStatus && matchesSource;
  });

  // Debug: Verificar quantos clientes reais existem
  console.log('📊 Clientes reais (tabela clients):', clients.length);
  console.log('📊 Leads convertidos (leads com status converted):', allLeads.filter(l => l.status === 'converted').length);
  console.log('📊 Sources dos clientes:', clients.map(c => c.source));

  // Filtrar leads ativos usando dados do Supabase
  const filteredLeads = activeLeads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (lead.phone && lead.phone.includes(searchTerm));
    
    const matchesStatus = !filterStatus || lead.status === filterStatus;
    const matchesSource = !filterSource || lead.source.toLowerCase().includes(filterSource.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesSource;
  });

  // Filtrar leads perdidos usando dados do Supabase
  const filteredLostLeads = lostLeads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (lead.phone && lead.phone.includes(searchTerm));
    
    const matchesSource = !filterSource || lead.source.toLowerCase().includes(filterSource.toLowerCase());
    
    return matchesSearch && matchesSource;
  });

  // Estatísticas baseadas nos dados reais do Supabase
  const stats = {
    totalClients: clients.length,
    totalLeads: activeLeads.length,
    lostLeads: lostLeads.length,
    newLeads: allLeads.filter(lead => lead.status === 'new').length,
    activeClients: getClientsByStatus('client').length,
    prospects: getClientsByStatus('prospect').length
  };

  const handleSaveClient = (client: any) => {
    const clientData = {
      ...client,
      created_at: client.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    if (editingClient) {
      updateClient(clientData);
    } else {
      addClient(clientData);
    }
    setShowClientForm(false);
    setEditingClient(undefined);
  };


  const handleEditClient = (client: any) => {
    setEditingClient(client);
    setShowClientForm(true);
  };

  // Remover função desnecessária - leads convertidos não devem ser editados separadamente

  const handleDeleteClient = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      deleteClient(id);
    }
  };

  const handleDeleteLead = (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;

    // Detectar UUID v4
    const isUUID = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(val);

    // Se for lead vindo do banco (UUID ou presente na lista do hook), excluir via Supabase
    if (isUUID(id) || dbLeads?.some(l => l.id === id)) {
      deleteLeadFromDB?.(id);
    } else {
      // Lead não encontrado (não há mais contexto local)
      console.warn('Lead não encontrado:', id);
    }
  };

  const handleConvertLead = async (lead: any) => {
    // Detectar UUID v4 (lead do banco) vs lead local
    const isUUID = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(val);
    
    if (isUUID(lead.id) || dbLeads?.some(l => l.id === lead.id)) {
      // Lead do banco - usar função do hook que já preserva a origem
      const success = await convertLeadDB(lead.id);
      
      if (success) {
        // Conversão bem-sucedida
        
        // Lead convertido com sucesso
        
        // Cliente será criado automaticamente na tabela pelo hook convertLeadDB
        console.log('Lead convertido com sucesso');
      }
    } else {
      // Lead não encontrado
      console.warn('Lead local não encontrado:', lead.id);
    }
  };

  const handleReactivateLead = async (lead: any) => {
    try {
      // Usar a função correta do hook useLeads para leads do banco de dados
      if (lead.metadata?.portal_lead_id || lead.source !== 'manual') {
        await updateLeadStatus(lead.id, 'new');
      } else {
        // Para leads locais do contexto
        const updatedLead = {
          ...lead,
          status: 'new' as const,
          message: `${lead.message}\n\n[REATIVADO em ${new Date().toLocaleDateString('pt-BR')}]`
        };
        console.log('Lead local reativado:', updatedLead);
      }
      
      toast({
        title: "Lead reativado",
        description: "Lead foi reativado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao reativar lead:', error);
      toast({
        title: "Erro",
        description: "Não foi possível reativar o lead",
        variant: "destructive",
      });
    }
  };

  const handleOpenEmailModal = (lead: any) => {
    setSelectedLeadForEmail(lead);
    setShowEmailModal(true);
  };

  const handleEmailSent = (lead: any, emailType: string) => {
    // Atualizar lead com informação do email enviado
    const updatedLead = {
      ...lead,
      message: `${lead.message}\n\n[EMAIL ${emailType.toUpperCase()} ENVIADO em ${new Date().toLocaleDateString('pt-BR')}]`
    };
    console.log('Email enviado para lead:', updatedLead);
  };

  const handleViewClientDetails = (client: any) => {
    setSelectedLeadForDetail(client);
    setShowLeadDetailModal(true);
  };

  const handleViewLeadDetails = (lead: any) => {
    setSelectedLeadForDetail(lead);
    setShowLeadDetailModal(true);
  };

  const getStatusColor = (status: string, type: 'client' | 'lead') => {
    if (type === 'client') {
      switch (status) {
        case 'lead': return 'bg-gray-100 text-gray-800';
        case 'prospect': return 'bg-blue-100 text-blue-800';
        case 'client': return 'bg-green-100 text-green-800';
        case 'inactive': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'new': return 'bg-yellow-100 text-yellow-800';
        case 'contacted': return 'bg-blue-100 text-blue-800';
        case 'qualified': return 'bg-purple-100 text-purple-800';
        case 'converted': return 'bg-green-100 text-green-800';
        case 'lost': return 'bg-red-100 text-red-800';
        case 'perdido': return 'bg-red-100 text-red-800';
        case 'archived': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'website': return 'bg-blue-100 text-blue-800';
      case 'olx': return 'bg-orange-100 text-orange-800';
      case 'portal': return 'bg-orange-100 text-orange-800';
      case 'portal grupo olx': return 'bg-orange-100 text-orange-800';
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'facebook': return 'bg-blue-100 text-blue-800';
      case 'instagram': return 'bg-pink-100 text-pink-800';
      case 'telefone': return 'bg-gray-100 text-gray-800';
      case 'manual': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const translateSource = (source: string) => {
    switch (source) {
      case 'website': return 'Site';
      case 'olx': return 'OLX';
      case 'portal': return 'Portal OLX';
      case 'whatsapp': return 'WhatsApp';
      case 'facebook': return 'Facebook';
      case 'instagram': return 'Instagram';
      case 'telefone': return 'Telefone';
      case 'manual': return 'Manual';
      default: return source;
    }
  };


  // Função para obter o nome do responsável
  const getResponsibleName = (assignedTo: string | undefined | null) => {
    if (!assignedTo) return 'Não atribuído';
    const user = getUserById(assignedTo);
    return user ? `${user.firstName} ${user.lastName}` : 'Usuário não encontrado';
  };

  const StatCard = ({ title, value, icon: Icon, color, change }: {
    title: string;
    value: string | number;
    icon: any;
    color: string;
    change?: string;
  }) => (
    <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className="text-xs sm:text-sm text-green-600 mt-1">
              <TrendingUp className="w-3 sm:w-4 h-3 sm:h-4 inline mr-1" />
              {change}
            </p>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-full ${color}`}>
          <Icon className="w-4 sm:w-6 h-4 sm:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header responsivo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Gestão de Clientes</h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
              Gerencie seus clientes de forma eficiente e organize seu pipeline de vendas
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setShowClientForm(true)}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold hidden sm:inline">Novo Cliente</span>
              <span className="font-semibold sm:hidden">Novo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards responsivos */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
        <StatCard
          title="Total de Clientes"
          value={stats.totalClients}
          icon={Users}
          color="bg-blue-500"
          change="+12% este mês"
        />
        <StatCard
          title="Total de Leads"
          value={stats.totalLeads}
          icon={UserPlus}
          color="bg-green-500"
          change="+8% este mês"
        />
        <StatCard
          title="Leads Arquivados"
          value={stats.lostLeads}
          icon={AlertCircle}
          color="bg-red-500"
        />
        <StatCard
          title="Novos Leads"
          value={stats.newLeads}
          icon={AlertCircle}
          color="bg-yellow-500"
        />
        <StatCard
          title="Clientes Ativos"
          value={stats.activeClients}
          icon={UserCheck}
          color="bg-green-500"
        />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('clients')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'clients'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Clientes ({clients.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'leads'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>Leads Ativos ({activeLeads.length})</span>
                {stats.newLeads > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {stats.newLeads}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('lost-leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'lost-leads'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4" />
                <span>Leads Arquivados ({lostLeads.length})</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, email ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {activeTab !== 'lost-leads' && (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                {activeTab === 'clients' ? (
                  <>
                    <option value="lead">Lead</option>
                    <option value="prospect">Prospect</option>
                    <option value="client">Cliente</option>
                    <option value="inactive">Inativo</option>
                  </>
                ) : (
                  <>
                    <option value="new">Novo</option>
                    <option value="contacted">Contatado</option>
                    <option value="qualified">Qualificado</option>
                    <option value="converted">Convertido</option>
                  </>
                )}
              </select>
            )}

            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">Todas as origens</option>
              <option value="website">Site</option>
              <option value="olx">OLX</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="manual">Manual</option>
            </select>

            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Filter className="w-4 h-4 mr-2" />
              Mais Filtros
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'clients' ? (
            // Clients List - incluindo leads convertidos
            filteredClients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contato
                      </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Origem
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Responsável
                       </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                   <tbody className="bg-white divide-y divide-gray-200">
                     {/* Clientes regulares */}
                     {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{client.name}</div>
                            <div className="text-sm text-gray-500">{client.location}</div>
                            {client.metadata?.tags && client.metadata.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {client.metadata.tags.slice(0, 2).map((tag: string, index: number) => (
                                  <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                    {tag}
                                  </span>
                                ))}
                                {client.metadata.tags.length > 2 && (
                                  <span className="text-xs text-gray-500">+{client.metadata.tags.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-900">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              {client.email}
                            </div>
                            <div className="flex items-center text-sm text-gray-900">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              {client.phone}
                            </div>
                          </div>
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(client.source)}`}>
                             {translateSource(client.source)}
                           </span>
                         </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-orange-600 font-medium text-xs">
                                  {getResponsibleName(client.assigned_to).split(' ').map(n => n[0]).join('').toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm text-gray-900">{getResponsibleName(client.assigned_to)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                           <div className="flex items-center justify-end space-x-2">
                             <button
                               onClick={() => handleViewClientDetails(client)}
                               className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                               title="Visualizar detalhes"
                             >
                               <Eye className="w-4 h-4" />
                             </button>
                             <button
                               onClick={() => handleEditClient(client)}
                               className="text-orange-600 hover:text-orange-800 p-1 rounded transition-colors"
                               title="Editar"
                             >
                               <Edit className="w-4 h-4" />
                             </button>
                             <button
                               onClick={() => handleDeleteClient(client.id)}
                               className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                               title="Excluir"
                             >
                               <Trash2 className="w-4 h-4" />
                             </button>
                           </div>
                         </td>
                       </tr>
                     ))}
                      {/* Apenas clientes reais da tabela clients */}
                    </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Nenhum cliente encontrado
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterStatus || filterSource 
                    ? 'Tente ajustar os filtros para encontrar mais clientes'
                    : 'Comece adicionando seu primeiro cliente'
                  }
                </p>
                <button
                  onClick={() => setShowClientForm(true)}
                  className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Adicionar Cliente
                </button>
              </div>
            )
          ) : activeTab === 'leads' ? (
            // Leads List - usando dados do Supabase
            leadsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <span className="ml-2 text-gray-600">Carregando leads...</span>
              </div>
            ) : filteredLeads.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lead
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Origem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Responsável
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            {lead.email && (
                              <div className="flex items-center text-sm text-gray-900">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                {lead.email}
                              </div>
                            )}
                            {lead.phone && (
                              <div className="flex items-center text-sm text-gray-900">
                                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                {lead.phone}
                              </div>
                            )}
                          </div>
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status, 'lead')}`}>
                            {lead.status === 'new' ? 'Novo' :
                             lead.status === 'contacted' ? 'Contatado' :
                             lead.status === 'qualified' ? 'Qualificado' :
                             lead.status === 'converted' ? 'Convertido' :
                             lead.status === 'archived' ? 'Arquivado' : 'Perdido'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
                             {translateSource(lead.source)}
                           </span>
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-blue-600 font-medium text-xs">
                                  {getResponsibleName(lead.assigned_to).split(' ').map(n => n[0]).join('').toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm text-gray-900">{getResponsibleName(lead.assigned_to)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString('pt-BR')}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                           <div className="flex items-center justify-end space-x-2">
                             <button
                               onClick={() => handleViewLeadDetails(lead)}
                               className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                               title="Visualizar detalhes"
                             >
                               <Eye className="w-4 h-4" />
                             </button>
                             {lead.status !== 'converted' && lead.status !== 'lost' && (
                               <button
                                 onClick={() => handleConvertLead(lead)}
                                 className="text-green-600 hover:text-green-800 p-1 rounded transition-colors"
                                 title="Converter em cliente"
                               >
                                 <UserCheck className="w-4 h-4" />
                               </button>
                             )}
                              <button
                                onClick={() => updateLeadStatus(lead.id, 'archived')}
                                className="text-gray-600 hover:text-gray-800 p-1 rounded transition-colors"
                                title="Arquivar"
                              >
                                <Archive className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteLead(lead.id)}
                                className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                           </div>
                         </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Nenhum lead encontrado
                </h3>
                 <p className="text-gray-600 mb-4">
                   {searchTerm || filterStatus || filterSource 
                     ? 'Tente ajustar os filtros para encontrar mais leads'
                     : 'Os leads aparecerão aqui quando chegarem pelo sistema'
                   }
                 </p>
              </div>
            )
            ) : !leadsLoading && (
            // Lost Leads List
            filteredLostLeads.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Leads Arquivados - Reative quando necessário</h3>
                  </div>
                  <p className="text-gray-700 mt-2">
                    Estes leads foram arquivados e podem ser reativados para nova abordagem ou transferidos para outro corretor.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredLostLeads.map((lead) => (
                    <div key={lead.id} className="border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-red-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{lead.name}</h3>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`${lead.status === 'archived' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'} px-2 py-1 rounded-full text-xs font-medium`}>
                              {lead.status === 'archived' ? 'Arquivado' : 'Perdido'}
                            </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
                                {translateSource(lead.source)}
                              </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        {lead.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            <span>{lead.email}</span>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-2" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                        <div className="flex items-start">
                          <MessageSquare className="w-4 h-4 mr-2 mt-0.5" />
                          <span className="line-clamp-2">{lead.message}</span>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 mb-4">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Ações de Reativação:</h4>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleReactivateLead(lead)}
                            className="w-full bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center justify-center space-x-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Reativar Lead</span>
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button className="bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 transition-colors text-xs">
                              Transferir Corretor
                            </button>
                            <button 
                              onClick={() => handleOpenEmailModal(lead)}
                              disabled={!lead.email}
                              className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              📧 Enviar Email
                            </button>
                          </div>
                        </div>
                      </div>

                       <div className="flex items-center justify-between pt-3 border-t border-red-200">
                         <div className="text-xs text-gray-500">
                           Arquivado em: {new Date(lead.created_at).toLocaleDateString('pt-BR')}
                        </div>
                         <div className="flex items-center space-x-2">
                           <button
                             onClick={() => handleDeleteLead(lead.id)}
                             className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                             title="Excluir permanentemente"
                           >
                             <Trash2 className="w-4 h-4" />
                           </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Nenhum lead arquivado
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterSource 
                    ? 'Nenhum lead arquivado encontrado com os filtros aplicados'
                    : 'Ótimo trabalho! Não há leads arquivados no momento.'
                  }
                </p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Forms */}
      {showClientForm && (
        <ClientForm
          client={editingClient}
          onSave={handleSaveClient}
          onCancel={() => {
            setShowClientForm(false);
            setEditingClient(undefined);
          }}
        />
      )}

      {/* Email Options Modal */}
      {showEmailModal && selectedLeadForEmail && (
        <EmailOptionsModal
          lead={selectedLeadForEmail}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedLeadForEmail(null);
          }}
          onEmailSent={handleEmailSent}
        />
      )}

      {/* Client Detail Modal - Now using SimpleLeadDetailModal for consistency */}

      {/* Lead Detail Modal */}
      {showLeadDetailModal && selectedLeadForDetail && (
        <SimpleLeadDetailModal
          lead={selectedLeadForDetail}
          isOpen={showLeadDetailModal}
          onClose={() => {
            setShowLeadDetailModal(false);
            setSelectedLeadForDetail(null);
          }}
        />
      )}
    </div>
  );
};

export default ClientList;